import React, { useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { observer } from 'mobx-react-lite';

import PropTypes from 'prop-types';
import _ from 'lodash';
import Cookies from 'js-cookie';

import { adminUrl } from '@common/utilities/helpers';
import config from '@common/config';

import DesktopHeader from './DesktopHeader';
import GenAIHeader from './GenAIHeader';
import UserInfo from './UserInfo';

import api from '../../../api';

const defaultTitle = (logo) => (
  <img
    style={{ width: '100%', height: '90%' }}
    id="comp-k1ktm2uximgimage"
    alt="bright new wordmark.png"
    datatype="image"
    itemProp="image"
    src={logo}
  />
);

const getDefaultHeaderMenuItems = (isCoach, isAdmin, permissions) => {
  const items = [
    {
      key: '',
      display: 'Home',
      link: '/',
    },
    !permissions?.isCertifyDisabled && {
      key: 'certify',
      display: 'Certify',
      link: '/certify',
    },
    {
      key: 'path',
      display: 'My Path',
      link: '/path',
    },
  ];

  if (!permissions?.isCoachDisabled && (isCoach || isAdmin)) {
    items.push({
      key: 'coach',
      display: 'Coach',
      link: '/coach',
    });
  }

  if (isCoach || isAdmin) {
    items.push({
      key: 'reports',
      display: 'Reporting',
      link: '/reports',
    });
  }

  return _.compact(items);
};

const HeaderPackage = ({
  overrideMenuItems,
  overrideTitle,
  isCoach,
  userStore: { isAdmin },
  lang,
  user,
  firstName,
  hideNav = false,
  totalNotificationsInboxCount,
  hasNewCertificationFeedback,
  inboxStore,
  onboardingStore,
  hidden,
  hasGenAIAnimationLoaded,
}) => {
  const location = useLocation();
  const history = useHistory();

  const [hoverKey, setHoverKey] = useState(null);

  const defaultHeaderMenuItems = getDefaultHeaderMenuItems(
    isCoach,
    isAdmin,
    user?.customerPermissions,
  );
  const headerMenuItems = overrideMenuItems || defaultHeaderMenuItems;
  const logo = _.get(user, 'customerTheme.logo', 'https://s3.amazonaws.com/bright.public/bright-logo-2.png');
  const title = overrideTitle || defaultTitle(
    onboardingStore.welcomePlacemat?.extra?.companyLogo || logo,
  );

  const toggleHoverOn = (key) => setHoverKey(key);
  const toggleHoverOff = () => setHoverKey(null);

  const goToLink = (link, event) => {
    setHoverKey(null);

    if (event) {
      event.stopPropagation();
    }

    history.push(link);
  };

  const goToLinkMarkRead = async (link, event) => {
    const type = link === '/certify?ref=button' ? 'certification' : 'moment';

    if (type !== 'certification') {
      await inboxStore.markAllRead(type, 'me');
      await inboxStore.getInboxNotificationData();
    }

    event.stopPropagation();
    history.push(link);
  };

  const handleChangeInstance = () => {
    Cookies.remove('selectedInstance');
    window.location.href = '/';
  };

  const handleAcademyLogin = async () => {
    const response = await api.authenticateToAcademy(user.token);

    if (response) {
      window.open(`${config.academyURL}/?token=${response.data.token}`, '_blank');
    }
  };

  const handleAdminLogin = async () => {
    const response = await api.getAdminAccessToken();

    if (response) {
      window.open(`${adminUrl()}/?token=${response.data.token}`, '_blank');
    }
  };

  const UserInfoDropdown = ({ isGenAI }) => ( // centralize the props passed to UserInfo
    <UserInfo
      hoverKey={hoverKey}
      toggleHoverOn={toggleHoverOn}
      toggleHoverOff={toggleHoverOff}
      goToLink={goToLink}
      goToLinkMarkRead={goToLinkMarkRead}
      handleChangeInstance={handleChangeInstance}
      handleAcademyLogin={handleAcademyLogin}
      handleAdminLogin={handleAdminLogin}
      lang={lang}
      user={user}
      firstName={firstName}
      isAdmin={isAdmin}
      isCoach={isCoach}
      totalNotificationsInboxCount={totalNotificationsInboxCount}
      hasNewCertificationFeedback={hasNewCertificationFeedback}
      isGenAI={isGenAI}
    />
  );

  if (location.pathname.includes('/ai-conversation') && hasGenAIAnimationLoaded) {
    return (
      <GenAIHeader
        title={title}
        totalNotificationsInboxCount={totalNotificationsInboxCount}
        isAdmin={isAdmin}
        isCoach={isCoach}
        hideNav={hideNav}
        setHoverKey={setHoverKey}
        goToLink={goToLink}
      >
        <UserInfoDropdown
          isGenAI
        />
      </GenAIHeader>
    );
  }

  return (
    <DesktopHeader
      hidden={hidden}
      menuItems={headerMenuItems}
      title={title}
      isCoach={isCoach}
      isAdmin={isAdmin}
      hideNav={hideNav}
      goToLink={goToLink}
      toggleHoverOff={toggleHoverOff}
      toggleHoverOn={toggleHoverOn}
      hoverKey={hoverKey}
      customerTheme={user.customerTheme}
    >
      <UserInfoDropdown />
    </DesktopHeader>
  );
};

HeaderPackage.propTypes = {
  overrideMenuItems: PropTypes.arrayOf(PropTypes.shape({})),
  overrideTitle: PropTypes.string,
  firstName: PropTypes.string,
  isCoach: PropTypes.bool,
  userStore: PropTypes.shape({
    isAdmin: PropTypes.bool,
  }),
  lang: PropTypes.shape({
    t: PropTypes.func,
  }),
  user: PropTypes.shape({
    token: PropTypes.string,
    customerPermissions: PropTypes.shape({
      isCoachDisabled: PropTypes.bool,
      isCertifyDisabled: PropTypes.bool,
    }),
    customerTheme: PropTypes.shape({
      bannerBackgroundImage: PropTypes.string,
      bannerButtonColor: PropTypes.string,
      bannerTextColor: PropTypes.string,
      commsBanner: PropTypes.string,
      ctaLink: PropTypes.string,
      ctaText: PropTypes.string,
      customDomain: PropTypes.string,
      footerText: PropTypes.string,
      headerBackgroundColor: PropTypes.string,
      headerLinksHoverBackgroundColor: PropTypes.string,
      headerTextColor: PropTypes.string,
      logo: PropTypes.string,
      siteTitleText: PropTypes.string,
      welcomeMessage: PropTypes.string,
      welcomeText: PropTypes.string,
    }),
  }),
  hideNav: PropTypes.bool,
  totalNotificationsInboxCount: PropTypes.number,
  hasNewCertificationFeedback: PropTypes.bool,
  inboxStore: PropTypes.shape({
    inboxListing: PropTypes.arrayOf(PropTypes.shape({})),
    markAllRead: PropTypes.func,
    getInboxNotificationData: PropTypes.func,
  }),
  onboardingStore: PropTypes.shape({
    welcomePlacemat: PropTypes.shape({
      extra: PropTypes.shape({
        companyLogo: PropTypes.string,
      }),
    }),
  }),
  hidden: PropTypes.bool,
  hasGenAIAnimationLoaded: PropTypes.bool,
};

export default observer(HeaderPackage);
